/**
 * API functions for survey management
 */

import { SURVEY_ID_OFFSET } from '../constants/surveyConstants';
import {
  SESSION_TOKEN_HEADER,
  readSessionToken,
  rememberSessionFromPayload,
  sessionIdFromUrl,
  withSessionToken,
} from './sessionToken';
import {
  ASYNC_ANALYSIS_CONFIG,
  ANALYSIS_TASK_STATES,
  COMPLETION_CONFIG,
} from '../config/constants';
import { fetchWithTimeoutAndRetry } from './resilientFetch';
import { SurveyValidationError } from './surveyValidation';
import type { ClientSignals } from './botDetection';
import type { SurveyTimerConfig } from '../features/humanSurvey/surveyTimer';
import type { SurveyAuthoringSettings } from '../features/humanSurvey/surveyAuthoringSettings';
import type { ParsedQuestion } from '../components/survey/types';
import type {
  DynamicSectionAnswerPayload,
  DynamicSectionAnswerResponse,
  DynamicSectionNextResponse,
} from '../features/humanSurvey/aiDynamicSection';

export {
  validateSurvey,
  SurveyValidationError,
  reportIsClean,
  hasBlockingErrors,
  autoFixableFixes,
  requiresTypedConfirmation,
} from './surveyValidation';
export type {
  ValidationIssue,
  ValidationReport,
  ValidateResult,
  IssueSeverity,
} from './surveyValidation';

/** Convert survey ID to share ID (Base64 encoded with offset and randomization) */

/**
 * `fetch`, plus the respondent's capability token when the URL is session-scoped.
 *
 * Wrapping once is the point: there are a dozen session calls in this file, each with its own
 * headers object, and a token added at eleven of them is a survey that breaks at the twelfth.
 * Non-session URLs pass straight through, so this is safe to use everywhere.
 */
async function sessionFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const sessionId = sessionIdFromUrl(url);
  const token = sessionId === null ? null : readSessionToken(sessionId);
  if (!token) return fetch(url, init);
  return fetch(url, {
    ...init,
    headers: {
      ...(init.headers as Record<string, string> | undefined),
      [SESSION_TOKEN_HEADER]: token,
    },
  });
}

export function surveyIdToShareId(surveyId: number): string {
  // Multiply survey ID by a large prime to spread out the values more
  const multipliedId = surveyId * 982451653; // Large prime number
  const offsetId = multipliedId + SURVEY_ID_OFFSET;
  const encoded = btoa(offsetId.toString()).replace(/=/g, '');
  return encoded;
}

/** Convert share ID back to survey ID */
export function shareIdToSurveyId(shareId: string): number {
  // Decode + validate so a malformed/foreign share link fails cleanly (this is the
  // respondent entry point) instead of throwing a raw DOMException from atob() or
  // returning a bogus non-integer id that then gets sent to the API.
  let decoded: string;
  try {
    // Add padding back if needed
    const padding = 4 - (shareId.length % 4);
    if (padding !== 4) {
      shareId += '='.repeat(padding);
    }
    decoded = atob(shareId);
  } catch {
    throw new Error('Invalid survey link');
  }

  const offsetId = parseInt(decoded, 10);
  // Reverse the offset + multiplication used in encoding.
  const surveyId = (offsetId - SURVEY_ID_OFFSET) / 982451653; // Same prime as in encoding
  if (!Number.isInteger(surveyId) || surveyId <= 0) {
    throw new Error('Invalid survey link');
  }
  return surveyId;
}

/**
 * The question shape, the rubric it may carry, and the enums they use all live in
 * `components/survey/types` now — one declaration for every authoring surface (spec 42, phase 3).
 * Re-exported here because this module is where the survey wire types are looked for.
 */
export type {
  ParsedQuestion,
  QuestionRubric,
  RubricPoint,
  ChoiceAction,
  ConjointAttribute,
  QuestionCondition,
  QuestionDisplayCondition,
} from '../components/survey/types';

/**
 * Rapid-fire mode for a block (spec 29): a timed section where the respondent answers as many of
 * the block's questions as they can before the clock runs out.
 *
 * Note there is no order flag here — "same order for everyone vs randomized" is the block's
 * existing `randomize_within`. A second control would only be able to disagree with it.
 */
export interface RapidFireConfig {
  enabled: boolean;
  duration_seconds: number;
  /** null = the whole block. Below the pool size, each respondent gets a random subset. */
  max_questions?: number | null;
  allow_skip: boolean;
  /** Silence before a spoken answer advances, in SECONDS; absent = the 2s default. */
  silence_seconds?: number;
  intro_text?: string;
}

/** Survey-level block metadata (question grouping / randomization). */
export interface SurveyBlockDefinition {
  id: string;
  label: string;
  randomize_within: boolean;
  rapid_fire?: RapidFireConfig | null;
  display_condition?: {
    logic?: 'and' | 'or';
    conditions?: Array<{
      source_question_id: string;
      operator: 'selected' | 'not_selected' | '>' | '>=' | '=' | '<' | '<=';
      value: string;
      option_key?: string;
      threshold?: number;
    }>;
  };
}

export interface Survey {
  id: number;
  name: string;
  title?: string;
  description?: string;
  instructions?: string;
  state: string;
  user_id: number;
  created_at: string;
  updated_at: string;
  published_at?: string;
  questions?: ParsedQuestion[];
  blocks?: SurveyBlockDefinition[] | null;
  sessions_count?: number;
  responses_count?: number;
  // True when the survey has reached its max_responses quota. On the public payload it lets the
  // respondent UI show the closed screen at load, including for in-progress resumes.
  quota_full?: boolean;
  /**
   * Survey-wide countdown (spec 35). Absent or null means the survey is not on a clock — the
   * backend stores nothing rather than an `enabled: false` object, so both shapes occur.
   */
  timer_config?: SurveyTimerConfig | null;
  redirect_config?: {
    redirect_base_url: string;
    tracking_param: { [key: string]: string };
    completion_params: { [key: string]: string } | null;
    screen_out_params: { [key: string]: string } | null;
    quota_full_params: { [key: string]: string } | null;
  };
  // Per-survey AI feature config (e.g. { redundancy_skip_enabled: true })
  ai_features?: {
    redundancy_skip_enabled?: boolean;
    redundancy_confidence_threshold?: number;
    // v3 breadth-first redundancy controls.
    // 'off' (default): never skip a whole question — every column is asked; only follow-up
    // suppression trims redundancy. 'carry_forward': skip the ask for follow-up-free covered
    // questions but FILL the cell with the respondent's own covering words. 'drop': legacy.
    redundancy_question_mode?: RedundancyQuestionMode;
    followup_suppression_enabled?: boolean;
    // Per-survey toggle for rubric-driven adaptive follow-up probing. Default off.
    adaptive_probing?: boolean;
    // Experimental conversational / discussion-guide style.
    survey_style?: SurveyStyle;
    discussion_guide?: DiscussionGuide;
  } | null;
  // Flattened flag exposed on the public survey payload for the respondent UI.
  ai_redundancy_skip_enabled?: boolean;
  redundancy_question_mode?: RedundancyQuestionMode;
  followup_suppression_enabled?: boolean;
  // Experimental: flattened onto the public survey payload so the respondent UI can route.
  survey_style?: SurveyStyle;
  discussion_guide?: DiscussionGuide | null;
}

// ---------------------------------------------------------------------------
// Experimental: conversational / discussion-guide survey style
// ---------------------------------------------------------------------------

export type SurveyStyle = 'traditional' | 'conversational';

// How the AI treats a whole question it judges already-answered (v3 breadth-first).
export type RedundancyQuestionMode = 'off' | 'carry_forward' | 'drop';

export interface MandatoryQuestion {
  id: string;
  text: string;
}

export interface DiscussionGuide {
  guide_markdown: string;
  mandatory_questions: MandatoryQuestion[];
  max_turns?: number;
}

/** One moderator turn returned by /moderator/next. */
export interface ModeratorNextResult {
  action: 'ask' | 'complete';
  turn_id?: string;
  question_text?: string | null;
  is_mandatory?: boolean;
  mandatory_question_id?: string | null;
  covered_topics?: string[];
}

/**
 * Client-measured engagement timings for one question (see useActiveQuestionTime).
 * Field names are the frozen wire contract shared with the backend.
 */
export interface ActiveTimeMetrics {
  active_time_ms: number;
  idle_time_ms: number;
  blur_count: number;
}

export interface ModeratorAnswerPayload {
  turn_id: string;
  moderator_question: string;
  response_text: string;
  is_mandatory?: boolean;
  mandatory_question_id?: string | null;
  covered_topics?: string[];
  input_mode?: string;
  question_start_time?: string;
  active_time_ms?: number;
  idle_time_ms?: number;
  blur_count?: number;
}

/** Conversational survey: get the moderator's next question (or completion). */
export async function getNextModeratorQuestion(sessionId: number): Promise<ModeratorNextResult> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/moderator/next`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' } },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`,
    );
  }
  return response.json();
}

/** Conversational survey: persist one moderator question + respondent answer. */
/**
 * Record one conversational turn.
 *
 * Carries the same `survey_timer_expired` flag the traditional write paths do — past the hard
 * stop the turn is accepted and not recorded — so callers must read it rather than treating a
 * `success` as proof the answer landed.
 */
export async function submitModeratorAnswer(
  sessionId: number,
  payload: ModeratorAnswerPayload,
): Promise<{ status: string; session: SurveySession; survey_timer_expired?: boolean }> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/moderator/answer`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`,
    );
  }
  return response.json();
}

export interface SurveySession {
  id: number;
  state: string;
  current_question_index: number;
  survey_id: number;
  responses: any[];
  created_at: string;
  updated_at: string;
  completed_at?: string;
  responses_count: number;
}

// `parseQuestionsForSurvey` removed (2026-08-20): it POSTed to /api/surveys/parse, which no
// backend route serves — the real parser is POST /parse-questions — and nothing in src/ called it.

/**
 * Create survey with approved questions
 */
/** The survey metadata the builder collects alongside the questions. */
export interface CreateSurveyMetadata {
  title?: string;
  description?: string;
  instructions?: string;
}

export async function createSurveyWithQuestions(
  name: string,
  questions: ParsedQuestion[],
  metadata: CreateSurveyMetadata,
  settings: SurveyAuthoringSettings,
): Promise<{ survey_id: number; survey_data: Survey }> {
  const requestBody: Record<string, unknown> = {
    name,
    questions,
  };

  const { title, description, instructions } = metadata;
  if (title) requestBody.title = title;
  if (description) requestBody.description = description;
  if (instructions) requestBody.instructions = instructions;

  if (settings.blocks !== undefined && settings.blocks !== null) {
    requestBody.blocks = settings.blocks;
  }
  if (settings.redirect_config) requestBody.redirect_config = settings.redirect_config;
  if (settings.max_responses !== null && settings.max_responses !== undefined) {
    requestBody.max_responses = settings.max_responses;
  }
  // Both of these were configurable on the create form and never sent (#701). `null` is not worth
  // sending at creation — there is no prior value to clear — so only a real value travels.
  if (
    settings.min_linkedin_connections !== null &&
    settings.min_linkedin_connections !== undefined
  ) {
    requestBody.min_linkedin_connections = settings.min_linkedin_connections;
  }
  if (settings.timer_config !== null && settings.timer_config !== undefined) {
    requestBody.timer_config = settings.timer_config;
  }

  const response = await sessionFetch(`${import.meta.env.VITE_API_URL}/api/surveys`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // A 422 here carries the same structured report the questions PUT returns, nested under
    // `detail` — so the generic branch below would read `errorData.error` as undefined and throw
    // the bare string "HTTP 422: Unprocessable Entity", losing the reason entirely. Now that the
    // create request carries a timer, that would mean no survey and no explanation.
    //
    // Unlike the update path, a create-time 422 means "rejected, nothing saved" — it is never a
    // typed-confirmation prompt, so callers must surface it, not offer to override it (ruling R4).
    const detail = errorData?.detail;
    if (response.status === 422 && detail && typeof detail === 'object' && detail.report) {
      throw new SurveyValidationError(detail.report, Boolean(detail.can_override), detail.error);
    }
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/** Get the user's surveys */
export async function getUserSurveys(): Promise<{ surveys: Survey[] }> {
  const response = await sessionFetch(`${import.meta.env.VITE_API_URL}/api/surveys`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/** Get specific survey details (authenticated) */
export async function getSurvey(surveyId: number): Promise<{ survey: Survey }> {
  const response = await sessionFetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/** Get survey details for public access (survey taking) */
export async function getPublicSurvey(surveyId: number): Promise<{ survey: Survey }> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/public`,
    {
      method: 'GET',
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Preserve the HTTP status so callers can tell a closed survey (403) from a
    // transient failure. FastAPI puts the reason under `detail`.
    const err = new Error(
      errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`,
    ) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  return response.json();
}

/**
 * Get specific survey by ID with full details (alias for getSurvey)
 */
export const getSurveyById = getSurvey;

/** Update survey state (draft, active, paused, completed) */
export async function updateSurveyState(
  surveyId: number,
  newState: string,
): Promise<{ survey: Survey }> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/state`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        state: newState,
      }),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const detail = errorData?.detail;
    // Activation gate: going live with structural errors returns a 422 carrying the report. Surface
    // it as a SurveyValidationError so the UI can list what must be fixed (never overridable).
    if (response.status === 422 && detail && typeof detail === 'object' && detail.report) {
      throw new SurveyValidationError(detail.report, Boolean(detail.can_override), detail.error);
    }
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Session start result types
 */
export type SessionStartStatus =
  | 'success'
  | 'resumed'
  | 'quota_full'
  | 'already_completed'
  | 'screened_out'
  // Fraud-gate outcomes (returned before a session is created):
  | 'blocked' // clear automation/headless browser
  | 'challenge_required' // borderline — needs a Turnstile challenge
  | 'geo_blocked'; // non-US IP (soft gate, retryable)

export interface SessionStartResult {
  status?: SessionStartStatus;
  session_id?: number;
  survey_id?: number;
  session_data?: SurveySession;
  redirect_url?: string;
  message?: string;
  reason?: string;
  error?: string;
}

/**
 * Start or resume a survey session
 * If an external_tracking_id is provided and a session already exists,
 * the existing session is returned based on its state.
 *
 * clientSignals (automation-detection traits) and turnstileToken (a completed
 * CAPTCHA challenge) feed the backend fraud gate.
 */
export async function startSurveySession(
  surveyId: number,
  externalTrackingId?: string,
  clientSignals?: ClientSignals,
  turnstileToken?: string,
): Promise<SessionStartResult> {
  const requestBody: any = {};

  if (externalTrackingId) {
    requestBody.external_tracking_id = externalTrackingId;
  }
  if (clientSignals) {
    requestBody.client_signals = clientSignals;
  }
  if (turnstileToken) {
    requestBody.turnstile_token = turnstileToken;
  }

  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/sessions`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  // The capability token is minted server-side when the session is created and arrives exactly
  // once, here. Nothing else in the app ever sees it again, so failing to store it at this single
  // point means every later session call goes out unauthenticated and is refused — which is the
  // whole failure this file exists to prevent.
  const started = await response.json();
  rememberSessionFromPayload(started);
  return started;
}

/**
 * Result of submitting a response or follow-up. The backend may signal a skip (either
 * rule-based conditional skip or AI redundancy skip) and/or session termination.
 */
// Adaptive question scoping: when an upcoming open-text question's rubric was already partly
// answered earlier, the backend narrows its initial ask and returns the override here (keyed by
// the upcoming question_id). The runner shows `adapted_question` instead of the authored text.
export interface AdaptedQuestion {
  question_id: string;
  adapted_question: string;
  authored_question?: string;
  covered_points?: string[];
  inferred_quotes?: string[];
  remaining_points?: string[];
}

export interface SubmitResponseResult {
  session_data: SurveySession;
  status?: 'success' | 'terminated' | string;
  /**
   * Set when the survey-wide hard stop had already passed, in which case NOTHING was written.
   *
   * The status is still `success` — deliberately, so the client stops retrying rather than
   * looping on an error — so this flag is the entire difference between an answer that was
   * recorded and one that was accepted and discarded. Read it with `isTimerExpiredWrite`.
   */
  survey_timer_expired?: boolean;
  skip_applied?: boolean;
  next_question_index?: number;
  skip_reason?: 'conditionally_skipped' | 'ai_skipped' | string;
  termination_reason?: string;
  redirect_url?: string;
  adapted_question?: AdaptedQuestion;
}

/**
 * Get existing session by ID (public endpoint for session resume)
 * Returns null if session not found or not in resumable state
 */
export async function getSessionById(sessionId: number): Promise<SurveySession | null> {
  try {
    const response = await sessionFetch(
      `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}`,
      {
        method: 'GET',
      },
    );

    if (!response.ok) {
      // Session not found or other error - return null to indicate not resumable
      return null;
    }

    const payload = await response.json();
    // The route replies {'session': {...}} (routes_fastapi/survey.py). Reading .state off the
    // ENVELOPE always gave undefined, so this returned null every time and each reload silently
    // started a brand-new session — the respondent lost every answer they had given. Unwrap, and
    // still accept a bare session so the client isn't coupled to one shape.
    const session = payload?.session ?? payload;
    rememberSessionFromPayload(payload);

    // Only return session if it's in a resumable state
    if (session?.state === 'in_progress') {
      return session;
    }

    return null;
  } catch {
    // Network error or other issue - return null
    return null;
  }
}

/**
 * A rejected survey request, carrying the status and — when the server sends one — a
 * machine-readable reason code.
 *
 * The status alone cannot say whether a rejection is one a RESPONDENT can act on:
 * `POST /sessions/{id}/responses` answers 400 to everything `process_response` refuses, from the
 * selection cap ("Select at most 3 option(s)") through session-state errors to a catch-all that
 * passes `str(e)` from an unhandled exception straight out. Only a code can separate them, so it
 * is read here and gated on at the call site.
 */
export class SurveyRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'SurveyRequestError';
  }
}

/**
 * The reason, and any machine-readable code, out of a rejected survey response body.
 *
 * `detail` first: FastAPI rejects a business rule with `{"detail": ...}` and never `{"error"}`, so
 * reading only `error` reduced every rejection this endpoint raises to "HTTP 400: Bad Request" and
 * threw the reason away.
 *
 * Three shapes are handled. A STRING detail is today's server (backend#691 tracks tagging these
 * with a code). An OBJECT detail is the tagged form, read now so the respondent-facing gate can
 * switch over without another frontend change. A LIST detail is FastAPI's own 422 field-error
 * body, deliberately ignored — stringifying that at a respondent is worse than saying nothing.
 */
function readRejection(body: unknown): { reason?: string; code?: string } {
  const data = (body ?? {}) as Record<string, unknown>;
  const asString = (value: unknown) => (typeof value === 'string' ? value : undefined);
  const topLevelCode = asString(data.code);

  const detail = data.detail;
  if (typeof detail === 'string') return { reason: detail, code: topLevelCode };
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const tagged = detail as Record<string, unknown>;
    return { reason: asString(tagged.message), code: asString(tagged.code) ?? topLevelCode };
  }
  if (Array.isArray(detail)) return { code: topLevelCode };
  return { reason: asString(data.error), code: topLevelCode };
}

/**
 * Submit response to survey session
 */
export async function submitResponse(
  sessionId: number,
  questionId: string,
  responseData: any,
  questionType: string,
  questionStartTime?: string,
  advanceQuestion?: boolean,
  inputMode?: string,
  activeTime?: ActiveTimeMetrics | null,
  /** A rapid-fire voice answer written before its transcript is back: 'pending' while the
   * transcription call is outstanding, 'failed' once it has given up. Omit for a normal answer
   * and for the later resubmission that patches the real transcript in — see
   * schemas/survey.py::SubmitResponseRequest.transcription_status. */
  transcriptionStatus?: 'pending' | 'failed',
): Promise<SubmitResponseResult> {
  const requestBody: any = {
    question_id: questionId,
    response_data: responseData,
    question_type: questionType,
  };

  if (questionStartTime) {
    requestBody.question_start_time = questionStartTime;
  }

  if (advanceQuestion !== undefined) {
    requestBody.advance_question = advanceQuestion;
  }

  if (inputMode) {
    requestBody.input_mode = inputMode;
  }

  if (activeTime) {
    requestBody.active_time_ms = activeTime.active_time_ms;
    requestBody.idle_time_ms = activeTime.idle_time_ms;
    requestBody.blur_count = activeTime.blur_count;
  }

  if (transcriptionStatus) {
    requestBody.transcription_status = transcriptionStatus;
  }

  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/responses`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const { reason, code } = readRejection(errorData);
    throw new SurveyRequestError(
      reason || `HTTP ${response.status}: ${response.statusText}`,
      response.status,
      code,
    );
  }

  return response.json();
}

/**
 * Complete survey session
 */
export interface RapidFireStartResult {
  block_id: string;
  started_at: string;
  ends_at: string;
  remaining_seconds: number;
  expired: boolean;
  /** True when this Start re-read an existing deadline (reload, double-tap) rather than creating one. */
  resumed: boolean;
}

/**
 * Begin (or resume) a rapid-fire block. The deadline is stamped SERVER-side, so a reload comes
 * back with the real remaining time and pausing the tab can't buy more. Idempotent — calling it
 * again returns the same `ends_at`.
 */
export async function startRapidFireBlock(
  sessionId: number,
  blockId: string,
): Promise<RapidFireStartResult> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/rapid-fire/start`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ block_id: blockId }),
    },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export interface RapidFireCloseResult {
  session_data: SurveySession;
  next_question_index: number;
  expired_question_id: string | null;
  not_reached_question_ids: string[];
  already_closed?: boolean;
}

/**
 * End a rapid-fire block. One call for both stop conditions: 'expired' (the clock) records the
 * question on screen as reached-but-incomplete, 'cap' (max questions) leaves nothing expired
 * because nobody was mid-question. Safe to retry — the server makes it idempotent.
 */
export async function closeRapidFireBlock(
  sessionId: number,
  blockId: string,
  reason: 'expired' | 'cap',
): Promise<RapidFireCloseResult> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/rapid-fire/close`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ block_id: blockId, reason }),
    },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export interface RapidFireQuestionStat {
  question_id: string;
  text: string;
  type: string;
  /** Served to the respondent — answered, skipped, or on screen when the clock stopped. */
  reached: number;
  completed: number;
  avg_response_time_seconds: number | null;
}

export interface RapidFireBlockSummary {
  block_id: string;
  label: string;
  duration_seconds: number;
  max_questions: number | null;
  allow_skip: boolean;
  pool_size: number;
  respondents: number;
  avg_questions_completed: number | null;
  avg_response_time_seconds: number | null;
  skipped_total: number;
  questions: RapidFireQuestionStat[];
}

/** Researcher-side rapid-fire aggregates (deterministic, no AI). */
export async function getRapidFireSummary(surveyId: number): Promise<{
  survey_id: number;
  blocks: RapidFireBlockSummary[];
}> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/rapid-fire-summary`,
    { method: 'GET', credentials: 'include' },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}`);
  }
  return response.json();
}

/** Retried rather than bare: a dropped packet here strands a finished respondent. The token goes
 * in the header — the query-param form is reserved for the beacons (see sessionToken.ts). */
export async function completeSurveySession(sessionId: number): Promise<{
  session_data: SurveySession;
  is_complete: boolean;
  redirect_url?: string;
}> {
  const url = `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/complete`;
  const token = readSessionToken(sessionId);
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers[SESSION_TOKEN_HEADER] = token;

  const response = await fetchWithTimeoutAndRetry(
    url,
    { method: 'POST', headers },
    {
      timeoutMs: COMPLETION_CONFIG.TIMEOUT_MS,
      retries: COMPLETION_CONFIG.RETRIES,
      backoffMs: COMPLETION_CONFIG.BACKOFF_MS,
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get survey session details
 */
export async function getSurveySession(sessionId: number): Promise<{ session: SurveySession }> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}`,
    {
      method: 'GET',
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

const HEARTBEAT_CONTENT_TYPE = 'application/json';

/**
 * Whether the API is served from the page's own origin, as production's is.
 *
 * Asked for one reason: `application/json` is not a CORS-safelisted content type, so a request
 * carrying it cross-origin must be PREFLIGHTED — and this heartbeat's whole purpose is to leave
 * as the page is being torn down, which is precisely when a browser drops the preflight and with
 * it the request behind it. Same-origin there is no preflight to lose and `sendBeacon` is the
 * strongest guarantee available; cross-origin the exchange has to be one the platform promises to
 * keep alive as a whole, which is `fetch` with `keepalive`.
 *
 * Deliberately not solved by switching to `text/plain` — the endpoint parses a JSON body and
 * would reject it — so the split stands until the server can read both.
 */
const isSameOriginApi = (url: string): boolean => {
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
};

/**
 * Send heartbeat to keep session alive and track activity.
 *
 * What a heartbeat tells the server about the respondent right now.
 *
 * Both fields are advisory — the server decides whether to pause and clamps the idle time — but
 * they are not optional, because their ABSENCE is itself a claim: no body at all reaches
 * `apply_activity` as "not idle, not hidden", which resumes a paused clock.
 */
export interface SessionActivity {
  idle_ms: number;
  hidden: boolean;
}

export interface SurveyTimerView {
  remaining_seconds: number;
  paused: boolean;
  mode?: 'guidance' | 'hard_stop';
  visible?: boolean;
  duration_seconds?: number;
}

/**
 * A timer request that failed at the HTTP layer, with the status still attached.
 *
 * Both of the timer's own requests are retried on failure, and both retry FOR EVER — which is
 * right for a dropped packet and wrong for a 404. `new Error(\`...: ${response.status}\`)` put the
 * one fact that tells them apart inside a string nobody could branch on, so a session that has
 * been purged answers 404 and the tab keeps posting: every thirty seconds to /timer/expire, every
 * sixty to /timer/start, for the life of the page, on the unauthenticated respondent path.
 *
 * Carries the status and nothing else. Which statuses are worth asking again is decided by
 * `surveyTimer.isUnrecoverableRequestFailure`, beside the refusal lists it belongs with.
 */
export class SurveyTimerRequestError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'SurveyTimerRequestError';
    this.status = status;
  }
}

/**
 * What POST /timer/start says.
 *
 * The refusals come back as HTTP **200** with `{timer: null, reason}` — `session_not_in_progress`
 * when the abandonment sweep or screening has already ended the session, `survey_not_timed` when
 * the researcher cleared the clock. Collapsing that to a bare `null` made a permanent refusal
 * indistinguishable from a dropped request, so the tab retried it for the life of the page.
 * `surveyTimer.isTerminalStartRefusal` decides which reasons are worth asking again.
 */
export interface StartTimerResult {
  timer: SurveyTimerView | null;
  reason?: string;
}

/**
 * Start the survey-wide clock (spec 35).
 *
 * Called when the respondent reaches the first question, never on page load, so time spent on
 * the welcome and consent screens is not charged against the survey. Idempotent server-side: a
 * reload returns the ORIGINAL deadline rather than a fresh one.
 */
export async function startSurveyTimer(sessionId: number): Promise<StartTimerResult> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/timer/start`,
    { method: 'POST' },
  );
  if (!response.ok) {
    throw new SurveyTimerRequestError(
      `Failed to start survey timer: ${response.status}`,
      response.status,
    );
  }
  const body = await response.json();
  return { timer: body?.timer ?? null, reason: body?.reason };
}

/**
 * What POST /heartbeat says about the clock.
 *
 * The two `timer` cases are NOT the same thing, and the endpoint means them differently:
 *
 * - **absent** — this reply carried no timer news (an untimed session, an older or reshaped
 *   envelope). Whatever the client has stands.
 * - **explicitly null** — the clock is GONE. The route resolves the reading against the survey's
 *   CURRENT config, and `public_view` returns null once that config is cleared: "an explicit null
 *   says the clock is gone; public_view returns exactly that." It is the server's fix for a
 *   respondent counting down a deadline the researcher deleted.
 *
 * Reading both as `null` — which `.timer ?? null` did — throws the second one away.
 */
export interface TimerHeartbeatResult {
  timer?: SurveyTimerView | null;
}

/**
 * The heartbeat, carrying what the timer needs and returning the authoritative remaining time.
 *
 * Deliberately not a second poll: this request already fires every 60s for liveness, and the
 * visible countdown is interpolated locally from the number it returns. `idle_ms` and `hidden`
 * are advisory — the server decides whether to pause and clamps the reported idle time.
 */
export async function sendTimerHeartbeat(
  sessionId: number,
  activity: SessionActivity,
): Promise<TimerHeartbeatResult> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/heartbeat`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(activity),
    },
  );
  if (!response.ok) throw new Error(`Heartbeat failed: ${response.status}`);
  const body = await response.json();
  // The key's PRESENCE is the signal, so it is tested rather than read: `body.timer ?? null`
  // would turn "no timer news" into "the clock is gone" and clear a live countdown.
  if (!body || typeof body !== 'object' || !('timer' in body)) return {};
  return { timer: body.timer ?? null };
}

/**
 * What the server says when asked to end a timed session.
 *
 * On a granted expiry this is the SAME envelope POST /complete returns — `redirect_url` and all
 * — because a respondent who ran out of time on a panel still has to be returned to the panel to
 * be credited. On a refusal it carries the reason and a fresh reading to re-sync from.
 */
export interface ExpireTimerResult {
  expired: boolean;
  /**
   * Whether the granted stop became a real COMPLETION, or was retired unpaid.
   *
   * Present on every `expired: true` reply. A hard stop is anchored on the session's own
   * `created_at`, so a short one can run out while the respondent is still on welcome or consent
   * — and a session that recorded nothing is not a completion: the server retires it instead of
   * burning a `max_responses` slot and paying a panel for an empty row. `credited: false` comes
   * with `redirect_url: null`, and the completion page must not tell that respondent their
   * answers were saved.
   */
  credited?: boolean;
  /**
   * Why the server refused, when it did.
   *
   * Left as a bare `string` on purpose rather than a union with `| string` bolted on, which
   * collapses to `string` anyway and only LOOKS exhaustive. Which reasons are final is decided by
   * `isTerminalExpiryRefusal` in surveyTimer.ts, against an explicit list — so an unrecognised
   * reason keeps the client retrying instead of silently cancelling the deadline.
   */
  reason?: string;
  timer?: SurveyTimerView | null;
  session_data?: SurveySession;
  is_complete?: boolean;
  redirect_url?: string;
}

/**
 * Ask the server to end a hard-stop session whose clock has run out.
 *
 * A request, not an instruction: the server re-derives the deadline and refuses if it disagrees,
 * so a client with a skewed clock cannot end anyone's survey early.
 */
export async function expireSurveyTimer(sessionId: number): Promise<ExpireTimerResult | null> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/timer/expire`,
    { method: 'POST' },
  );
  if (!response.ok) {
    throw new SurveyTimerRequestError(
      `Failed to expire survey timer: ${response.status}`,
      response.status,
    );
  }
  return await response.json();
}

/**
 * The liveness ping, and what it says about the respondent.
 *
 * The activity is REQUIRED rather than optional, because a heartbeat with no body is not silence:
 * the route reads it as `idle_ms=None, hidden=False`, which is `apply_activity`'s definition of
 * "the respondent is here" — so it takes the resume branch and restarts a clock the timer had
 * paused. A bodyless ping from a tab that is closing therefore set the survey draining while
 * nobody was there. Every caller now has to say what it actually knows.
 */
export function sendSessionHeartbeat(sessionId: number, activity: SessionActivity): void {
  const url = `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/heartbeat`;
  const body = JSON.stringify(activity);
  // Same-origin only. A typed Blob, not a bare string: sendBeacon sends text/plain otherwise,
  // which the endpoint's JSON body cannot be parsed from — but that type is also what takes the
  // request off the CORS-simple list, so cross-origin the beacon buys a preflight it then loses
  // to the page teardown it exists to survive. There the keepalive fetch below is the one the
  // platform undertakes to finish.
  // The RETURN VALUE is the whole guarantee. `sendBeacon` answers false when the user agent
  // declines to queue the request at all — the per-origin beacon quota, a payload over the limit,
  // some teardown states — and it was thrown away, so the ping simply vanished with nothing behind
  // it. On `beforeunload` this is the LAST activity mark of the session, and a missing one leaves
  // the server's view of idle/hidden stale: `apply_activity` takes its resume branch and a clock
  // the timer had paused starts draining with the tab already closed.
  if (
    navigator.sendBeacon &&
    isSameOriginApi(url) &&
    navigator.sendBeacon(withSessionToken(url), new Blob([body], { type: HEARTBEAT_CONTENT_TYPE }))
  ) {
    return;
  }
  // The real `fetch`, not the wrapper: this path is entered while the tab is tearing down, and it
  // depends on the promise the platform hands back (keepalive, and a rejection that must stay
  // swallowed). An async wrapper would re-wrap that promise. The token rides in the query string
  // instead — the same way the beacon above has to carry it, since neither can set a header.
  fetch(withSessionToken(url), {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': HEARTBEAT_CONTENT_TYPE },
    body,
  }).catch(() => {});
}

/** Last-ditch completion mark for a tab closed mid-`/complete`, over `sendSessionHeartbeat`'s path. */
export function sendCompletionBeacon(sessionId: number): boolean {
  const url = withSessionToken(
    `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/complete`,
  );
  if (navigator.sendBeacon && isSameOriginApi(url) && navigator.sendBeacon(url, '')) {
    return true;
  }
  fetch(url, { method: 'POST', keepalive: true }).catch(() => {});
  return false;
}

/**
 * Submit follow-up response to existing question.
 * `activeTime` carries the engagement accrued SINCE THE PREVIOUS SUBMISSION for this
 * question (main answer or prior follow-up) — a delta, not the per-question total.
 */
export async function submitFollowupResponse(
  sessionId: number,
  questionId: string,
  followupQuestion: string,
  followupResponse: string,
  activeTime?: ActiveTimeMetrics | null,
): Promise<SubmitResponseResult> {
  const requestBody: Record<string, unknown> = {
    question_id: questionId,
    followup_question: followupQuestion,
    followup_response: followupResponse,
  };

  if (activeTime) {
    requestBody.active_time_ms = activeTime.active_time_ms;
    requestBody.idle_time_ms = activeTime.idle_time_ms;
    requestBody.blur_count = activeTime.blur_count;
  }

  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/followup`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Start async analysis of response for potential follow-up question
 */
export async function startAnalyzeResponseForFollowup(
  sessionId: number,
  questionId: string,
  questionText: string,
  userResponse: string,
  questionType: string,
  context?: string,
  imageUrls?: string[],
): Promise<{
  status: 'processing';
  task_id: string;
  message: string;
}> {
  const requestBody: Record<string, unknown> = {
    question_id: questionId,
    question_text: questionText,
    user_response: userResponse,
    question_type: questionType,
    context: context,
  };

  // Include image URLs for photo_upload questions (for vision-based followup analysis)
  if (imageUrls && imageUrls.length > 0) {
    requestBody.image_urls = imageUrls;
  }

  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/analyze-response`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Check status of analysis task
 */
export async function getAnalysisTaskStatus(
  sessionId: number,
  taskId: string,
): Promise<{
  status: (typeof ANALYSIS_TASK_STATES)[keyof typeof ANALYSIS_TASK_STATES];
  analysis?: {
    needs_followup: boolean;
    reason: string;
    followup_question?: string;
  };
  error?: string;
  message: string;
}> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/analysis-tasks/${taskId}`,
    {
      method: 'GET',
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Poll for analysis completion with timeout and progress callbacks
 */
export async function pollForAnalysisCompletion(
  sessionId: number,
  taskId: string,
  options: {
    timeout?: number; // milliseconds, default 60000 (60 seconds)
    pollInterval?: number; // milliseconds, default 1000 (1 second)
    onProgress?: (status: string, message: string) => void;
  } = {},
): Promise<{
  analysis: {
    needs_followup: boolean;
    reason: string;
    followup_question?: string;
  };
}> {
  const {
    timeout = ASYNC_ANALYSIS_CONFIG.POLL_TIMEOUT_MS,
    pollInterval = ASYNC_ANALYSIS_CONFIG.POLL_INTERVAL_MS,
    onProgress,
  } = options;

  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    try {
      const result = await getAnalysisTaskStatus(sessionId, taskId);

      // Notify progress callback
      if (onProgress) {
        onProgress(result.status, result.message);
      }

      if (result.status === ANALYSIS_TASK_STATES.SUCCESS) {
        if (result.analysis) {
          return { analysis: result.analysis };
        } else {
          throw new Error('Analysis completed but no analysis data received');
        }
      } else if (result.status === ANALYSIS_TASK_STATES.FAILED) {
        throw new Error(result.error || 'Analysis task failed');
      }

      // Task is still pending or processing, wait before next poll
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    } catch (error) {
      // If it's a network error, retry after a short delay
      if (
        error instanceof Error &&
        (error.message.includes('fetch') || error.message.includes('NetworkError'))
      ) {
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
        continue;
      }
      // Other errors should be thrown immediately
      throw error;
    }
  }

  throw new Error(`Analysis timeout after ${timeout / 1000} seconds`);
}

/**
 * Analyze response for potential follow-up question (maintains backward compatibility)
 * Now uses async processing with polling
 */
export async function analyzeResponseForFollowup(
  sessionId: number,
  questionId: string,
  questionText: string,
  userResponse: string,
  questionType: string,
  options?: {
    timeout?: number;
    pollInterval?: number;
    context?: string;
    imageUrls?: string[];
    onProgress?: (status: string, message: string) => void;
  },
): Promise<{
  analysis: {
    needs_followup: boolean;
    reason: string;
    followup_question?: string;
  };
}> {
  // Start the analysis
  const taskResponse = await startAnalyzeResponseForFollowup(
    sessionId,
    questionId,
    questionText,
    userResponse,
    questionType,
    options?.context,
    options?.imageUrls,
  );

  // Poll for completion
  return pollForAnalysisCompletion(sessionId, taskResponse.task_id, options);
}

/**
 * Get all responses for a survey - simple list view
 */
export async function getSurveyResponses(surveyId: number): Promise<{
  survey_id: number;
  total_responses: number;
  total_attempts?: number;
  median_active_time_seconds?: number | null;
  responses: Array<{
    session_id: number;
    state?: string;
    response_number?: number | null;
    completed_at: string | null;
    started_at?: string | null;
    last_activity_at?: string | null;
    responses_count: number;
    time_spent_seconds?: number | null;
    active_time_seconds?: number | null;
    external_tracking_id?: string | null;
    customer_id?: string | number | null;
  }>;
}> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/responses`,
    {
      method: 'GET',
      credentials: 'include',
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

interface ResponseItem {
  type:
    | 'single_choice'
    | 'multiple_choice'
    | 'yes_no'
    | 'rating_scale'
    | 'likert_scale'
    | 'short_text'
    | 'long_text'
    | 'dropdown'
    | 'nps'
    | 'numeric_input'
    | 'ranking'
    | 'matrix_rating'
    | 'max_diff'
    | 'conjoint_analysis'
    | 'original_response'
    | 'followup_question'
    | 'followup_response';
  value: any;
  timestamp: string;
}

/**
 * Get detailed responses for a specific session
 */
export async function getSessionResponses(sessionId: number): Promise<{
  session_id: number;
  completed_at: string;
  responses: Array<{
    question_id?: string;
    question_text: string;
    question_type: string;
    response_data: ResponseItem[];
    created_at: string;
    /** Carries `mode` (one-screen best/worst vs multi-set) and the researcher's anchor words. */
    max_diff_config?: {
      mode?: string;
      best_label?: string;
      worst_label?: string;
    } | null;
    state?: string; // includes 'ai_skipped'
    ai_inference?: {
      inferred_answer?: any;
      covered_by?: string[];
      citation?: string;
      confidence?: number;
      reason?: string;
    };
  }>;
}> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/responses`,
    {
      method: 'GET',
      credentials: 'include',
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get aggregated MaxDiff summary for a survey across all sessions
 */
export async function getSurveyMaxDiffSummary(surveyId: number): Promise<{
  survey_id: number;
  maxdiff_questions: Array<{
    question_id: string;
    question_text: string;
    total_sessions: number;
    total_sets: number;
    claims: Array<{
      claim: string;
      most_count: number;
      least_count: number;
      net_score: number;
      most_percentage: number;
      least_percentage: number;
      rank: number;
    }>;
  }>;
}> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/maxdiff-summary`,
    {
      method: 'GET',
      credentials: 'include',
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get aggregated Image Hotspot summary for a survey across all sessions
 */
export async function getSurveyImageHotspotSummary(surveyId: number): Promise<{
  survey_id: number;
  image_hotspot_questions: Array<{
    question_id: string;
    question_text: string;
    image_url: string;
    bounding_boxes: Array<{
      id: string;
      label: string;
      x: number;
      y: number;
      width: number;
      height: number;
    }>;
    ranking_count: number;
    total_sessions: number;
    total_clicks: number;
    aggregated_clicks: Array<{
      x: number;
      y: number;
      rank: number;
      product_id: string | null;
      product_label: string | null;
      timestamp_ms: number;
    }>;
    response_distribution: Array<{
      option: string;
      count: number;
      percentage: number;
      ranking_breakdown: Record<string, number>;
    }>;
  }>;
}> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/image-hotspot-summary`,
    {
      method: 'GET',
      credentials: 'include',
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update survey questions, quota, and redirect config
 */
export async function updateSurveyQuestions(
  surveyId: number,
  questions: any[],
  redirectConfig?: any,
  maxResponses?: number | null,
  blocks?: SurveyBlockDefinition[] | null,
  minLinkedInConnections?: number | null,
  enableAiRedundancySkip?: boolean,
  surveyStyle?: SurveyStyle,
  discussionGuide?: DiscussionGuide | null,
  redundancyQuestionMode?: RedundancyQuestionMode,
  followupSuppressionEnabled?: boolean,
  acknowledgeWarnings?: boolean,
  adaptiveProbing?: boolean,
  crossQuestionCoverage?: boolean,
  adaptiveQuestionScoping?: boolean,
  surveyLevelProbingEnabled?: boolean,
  // Survey-wide countdown (spec 35). Three-state on purpose, matching the server's rule for this
  // field: a config SETS the timer, an explicit `null` CLEARS it, and `undefined` leaves whatever
  // is stored untouched. Passing `null` where you meant `undefined` silently switches a live
  // survey's clock off.
  timerConfig?: SurveyTimerConfig | null,
): Promise<{
  survey: any;
  report?: import('./surveyValidation').ValidationReport;
}> {
  const body: any = { questions };

  if (blocks !== undefined) {
    body.blocks = blocks;
  }
  if (redirectConfig !== undefined) {
    body.redirect_config = redirectConfig;
  }

  if (maxResponses !== undefined) {
    body.max_responses = maxResponses;
  }

  if (enableAiRedundancySkip !== undefined) {
    body.enable_ai_redundancy_skip = enableAiRedundancySkip;
  }

  if (redundancyQuestionMode !== undefined) {
    body.redundancy_question_mode = redundancyQuestionMode;
  }

  if (followupSuppressionEnabled !== undefined) {
    body.followup_suppression_enabled = followupSuppressionEnabled;
  }

  if (adaptiveProbing !== undefined) {
    body.adaptive_probing = adaptiveProbing;
  }

  if (crossQuestionCoverage !== undefined) {
    body.cross_question_coverage = crossQuestionCoverage;
  }

  if (adaptiveQuestionScoping !== undefined) {
    body.adaptive_question_scoping = adaptiveQuestionScoping;
  }

  if (surveyLevelProbingEnabled !== undefined) {
    body.survey_level_probing_enabled = surveyLevelProbingEnabled;
  }

  // Experimental conversational style (stored in ai_features server-side).
  if (surveyStyle !== undefined) {
    body.survey_style = surveyStyle;
  }
  if (discussionGuide !== undefined && discussionGuide !== null) {
    body.discussion_guide = discussionGuide;
  }
  if (acknowledgeWarnings !== undefined) {
    body.acknowledge_warnings = acknowledgeWarnings;
  }

  if (minLinkedInConnections !== undefined) {
    body.min_linkedin_connections = minLinkedInConnections;
  }

  // `null` is meaningful here and must survive to the wire — it is how a timer is removed. The
  // `!== undefined` guard is what keeps an untouched survey's timer untouched.
  if (timerConfig !== undefined) {
    body.timer_config = timerConfig;
  }

  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/questions`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // A 422 from the referential-integrity gate carries a structured report under `detail`.
    const detail = errorData?.detail;
    if (response.status === 422 && detail && typeof detail === 'object' && detail.report) {
      throw new SurveyValidationError(detail.report, Boolean(detail.can_override), detail.error);
    }
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// Authoring-time redundancy audit (ADVISORY — never writes skip flags itself;
// the author must explicitly accept "Auto-skip duplicates" per cluster)
// ---------------------------------------------------------------------------

/** Mirror of the backend's NEVER_SKIP_QUESTION_TYPES (interview_constants.py): question
 * types that can never be skipped/auto-answered — a number/grid/ranking/diary cannot be
 * reconstructed from prose. The UI must never flag these for skipping. */
export const NEVER_SKIP_QUESTION_TYPES = new Set([
  'rating_scale',
  'likert_scale',
  'nps',
  'numeric_input',
  'matrix_rating',
  'ranking',
  'max_diff',
  'conjoint_analysis',
  'percentage_allocation',
  'image_hotspot',
  'photo_upload',
  'info_screen',
]);

export interface RedundancyCluster {
  question_ids: string[];
  theme: string;
  severity: 'duplicate' | 'strong' | 'mild' | string;
  suggestion: string;
}

/** Run the redundancy audit over a survey's question list (no responses needed).
 * Also generates + persists per-question info_targets server-side and returns the updated
 * questions array — callers MUST use that array as their mapping base. */
/** The researcher's answer to "your checklist no longer matches your goal" — the routes and the
 *  buttons existed, but nothing connected them until now. See ADR-0002 §5. */
async function postDynamicSectionRubricChoice(
  surveyId: number,
  questionId: string,
  choice: 'regenerate' | 'keep',
): Promise<{ survey_id: number; question_id: string; section: any }> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/questions/${encodeURIComponent(questionId)}/rubric/${choice}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`,
    );
  }
  return response.json();
}

export async function regenerateDynamicSectionRubric(surveyId: number, questionId: string) {
  return postDynamicSectionRubricChoice(surveyId, questionId, 'regenerate');
}

export async function keepDynamicSectionRubric(surveyId: number, questionId: string) {
  return postDynamicSectionRubricChoice(surveyId, questionId, 'keep');
}

export async function auditSurveyRedundancy(surveyId: number): Promise<{
  survey_id: number;
  question_count: number;
  clusters: RedundancyCluster[];
  questions?: any[];
}> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/redundancy-audit`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Survey Groups (between-subject distribution layer)
// ---------------------------------------------------------------------------

export interface SurveyGroup {
  id: number;
  name: string;
  group_count: number;
  group_labels: string[];
  survey_ids: number[];
  questions: any[];
  blocks?: SurveyBlockDefinition[] | null;
  allocation_ratios?: number[] | null;
  min_linkedin_connections?: number | null;
  user_id: number;
  created_at: string;
  updated_at: string;
  // Adaptive config surfaced from the child surveys (kept in sync on save).
  ai_features?: {
    adaptive_probing?: boolean;
    survey_level_probing_enabled?: boolean;
    [k: string]: unknown;
  } | null;
  // Per-survey settings surfaced from the child surveys so the group editor loads their real
  // state. Without these the redirect form loads blank and the next save nulls the children's
  // panel redirect — see the round-trip fix in SurveyGroupDetails.
  redirect_config?: {
    redirect_base_url: string;
    screened_out_url?: string | null;
    tracking_param: { [key: string]: string };
    completion_params: { [key: string]: string } | null;
    screen_out_params: { [key: string]: string } | null;
    quota_full_params: { [key: string]: string } | null;
  } | null;
  max_responses?: number | null;
}

export interface SurveyGroupCreatePayload {
  name: string;
  group_count: number;
  questions: any[];
  blocks?: SurveyBlockDefinition[] | null;
  title?: string;
  description?: string;
  instructions?: string;
  redirect_config?: any;
  max_responses_per_group?: number | null;
  allocation_ratios?: number[] | null;
  min_linkedin_connections?: number | null;
}

export async function createSurveyGroup(
  payload: SurveyGroupCreatePayload,
): Promise<{ group_id: number; group: SurveyGroup; distribution_url: string }> {
  const response = await sessionFetch(`${import.meta.env.VITE_API_URL}/api/survey-groups`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function getSurveyGroups(): Promise<{ groups: SurveyGroup[] }> {
  const response = await sessionFetch(`${import.meta.env.VITE_API_URL}/api/survey-groups`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function getSurveyGroupDetail(groupId: number): Promise<{
  group: SurveyGroup;
  session_counts: Record<number, { total: number; completed: number }>;
  welcome_data?: { title?: string; description?: string; instructions?: string };
}> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/survey-groups/${groupId}`,
    {
      method: 'GET',
      credentials: 'include',
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

export function getSurveyGroupExportUrl(groupId: number): string {
  return `${import.meta.env.VITE_API_URL}/api/survey-groups/${groupId}/export/xlsx`;
}

export async function getSurveyGroupResponses(groupId: number): Promise<{
  group_id: number;
  total_responses: number;
  responses: any[];
  per_group: Record<number, { label: string; responses: any[] }>;
}> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/survey-groups/${groupId}/responses`,
    {
      method: 'GET',
      credentials: 'include',
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function updateSurveyGroupQuestions(
  groupId: number,
  questions: any[],
  redirectConfig?: any,
  maxResponses?: number | null,
  allocationRatios?: number[] | null,
  blocks?: SurveyBlockDefinition[] | null,
  acknowledgeWarnings?: boolean,
  adaptiveProbing?: boolean,
  crossQuestionCoverage?: boolean,
  adaptiveQuestionScoping?: boolean,
  enableAiRedundancySkip?: boolean,
  welcomeData?: { title?: string; description?: string; instructions?: string },
  surveyLevelProbingEnabled?: boolean,
): Promise<{ group: SurveyGroup; report?: import('./surveyValidation').ValidationReport }> {
  const body: any = { questions };
  if (blocks !== undefined) body.blocks = blocks;
  if (redirectConfig !== undefined) body.redirect_config = redirectConfig;
  if (maxResponses !== undefined) body.max_responses = maxResponses;
  if (allocationRatios !== undefined) body.allocation_ratios = allocationRatios;
  if (acknowledgeWarnings !== undefined) body.acknowledge_warnings = acknowledgeWarnings;
  if (adaptiveProbing !== undefined) body.adaptive_probing = adaptiveProbing;
  if (crossQuestionCoverage !== undefined) body.cross_question_coverage = crossQuestionCoverage;
  if (adaptiveQuestionScoping !== undefined)
    body.adaptive_question_scoping = adaptiveQuestionScoping;
  if (enableAiRedundancySkip !== undefined) body.enable_ai_redundancy_skip = enableAiRedundancySkip;
  if (surveyLevelProbingEnabled !== undefined)
    body.survey_level_probing_enabled = surveyLevelProbingEnabled;
  // Welcome screen fields (propagated to all child surveys)
  if (welcomeData?.title !== undefined) body.title = welcomeData.title;
  if (welcomeData?.description !== undefined) body.description = welcomeData.description;
  if (welcomeData?.instructions !== undefined) body.instructions = welcomeData.instructions;

  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/survey-groups/${groupId}/questions`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const detail = err?.detail;
    if (response.status === 422 && detail && typeof detail === 'object' && detail.report) {
      throw new SurveyValidationError(detail.report, Boolean(detail.can_override), detail.error);
    }
    throw new Error(
      (detail && typeof detail === 'object' ? detail.error : detail) || `HTTP ${response.status}`,
    );
  }
  return response.json();
}

/** Dry-run structural validation for a survey group's master questions (no write). */
export async function validateSurveyGroup(
  groupId: number,
  questions: unknown[],
  blocks?: unknown[] | null,
  opts?: { applyAutoFixes?: boolean; fixes?: Record<string, unknown>[] },
): Promise<{ report: import('./surveyValidation').ValidationReport; fixed_questions?: unknown[] }> {
  const body: Record<string, unknown> = { questions };
  if (blocks !== undefined) body.blocks = blocks;
  if (opts?.applyAutoFixes) body.apply_auto_fixes = true;
  if (opts?.fixes && opts.fixes.length) body.fixes = opts.fixes;

  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/survey-groups/${groupId}/validate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const detail = err?.detail;
    throw new Error(
      (detail && typeof detail === 'object' ? detail.error : detail) || `HTTP ${response.status}`,
    );
  }
  const data = await response.json();
  return {
    report: data.report ?? { errors: [], warnings: [] },
    fixed_questions: data.fixed_questions,
  };
}

/**
 * The bound the `survey.name` column enforces (VARCHAR(255)), mirrored from the backend's
 * `SURVEY_NAME_MAX_LENGTH`. Held here so the field can stop the author before the round trip does.
 */
export const SURVEY_NAME_MAX_LENGTH = 255;

/**
 * Rename a survey — the ONLY write path for `survey.name` after creation.
 *
 * 404, never 403, on someone else's survey: a 403 would confirm it exists. Mirrors the shipped
 * `rename_simulation`, with a typed body rather than that route's untyped `request.json()`.
 */
export async function renameSurvey(
  surveyId: number,
  name: string,
): Promise<{ survey: { id: number; name: string } }> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/rename`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  return response.json();
}

/**
 * Update survey welcome screen data (title, description, instructions)
 */
export async function updateSurveyWelcomeData(
  surveyId: number,
  welcomeData: {
    title?: string;
    description?: string;
    instructions?: string;
  },
): Promise<{
  survey: Survey;
}> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/welcome`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(welcomeData),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

// =============================================================================
// SURVEY ANALYSIS API
// =============================================================================

export interface SurveyAnalysisStatus {
  analysis_id: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  progress_message?: string;
  error_message?: string;
}

export interface DistributionItem {
  count: number;
  percentage: number;
  // Different question types use different field names for the label
  choice?: string; // SingleChoice, Dropdown
  rating?: number; // RatingScale
  level?: string; // LikertScale
  score?: number; // NPS
  value?: number; // Numeric
}

export interface RankingItem {
  item?: string; // Ranking
  option?: string; // MaxDiff
  average_rank?: number;
  first_place_count?: number;
  last_place_count?: number;
  most_count?: number; // MaxDiff
  least_count?: number; // MaxDiff
  net_score?: number; // MaxDiff
}

export interface MatrixItem {
  item: string;
  average: number;
  count: number;
  distribution?: Record<number, number>;
}

export interface PreferenceItem {
  choice: string;
  count: number;
  percentage: number;
}

export interface ProductItem {
  product: string;
  total_clicks: number;
  first_choice_count: number;
  rank_distribution?: Record<number, number>;
}

export interface QuestionAnalysis {
  question_id: string;
  question_text: string;
  type: string;
  total_responses: number;
  tldr?: string;
  synthesis?: string;
  counter_arguments?: string[];
  distribution?: DistributionItem[];
  statistics?: Record<string, number>;
  responses?: (string | number | Record<string, unknown>)[];
  followup_responses?: string[];
  // Additional fields for specific question types
  rankings?: RankingItem[]; // Ranking, MaxDiff
  items?: MatrixItem[]; // Matrix
  preferences?: PreferenceItem[]; // Conjoint
  products?: ProductItem[]; // Image Hotspot
  // Yes/No fields
  yes_count?: number;
  no_count?: number;
  yes_percentage?: number;
  no_percentage?: number;
  // Percentage Allocation fields
  allocation_target?: number;
}

export interface SurveySynthesis {
  markdown: string;
}

export interface SurveyAnalysisResult {
  analysis_id: number;
  survey_id: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  analysis_goal?: string;
  responses_analyzed: number;
  question_analyses?: Record<string, QuestionAnalysis>;
  survey_synthesis?: SurveySynthesis;
  created_at?: string;
  completed_at?: string;
  error_message?: string;
}

export interface AnalysisHistoryItem {
  analysis_id: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  analysis_goal?: string;
  responses_analyzed: number;
  created_at?: string;
  completed_at?: string;
}

/**
 * Trigger a new AI analysis for a survey
 */
export async function triggerSurveyAnalysis(
  surveyId: number,
  analysisGoal?: string,
): Promise<{
  analysis_id: number;
  task_id: string;
  status: string;
  message: string;
}> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/analysis`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        analysis_goal: analysisGoal,
      }),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  return response.json();
}

/**
 * Get the latest analysis for a survey
 */
export async function getSurveyAnalysis(surveyId: number): Promise<SurveyAnalysisResult> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/analysis`,
    {
      method: 'GET',
      credentials: 'include',
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  return response.json();
}

/**
 * Get a specific analysis by ID
 */
export async function getSurveyAnalysisById(
  surveyId: number,
  analysisId: number,
): Promise<SurveyAnalysisResult> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/analysis/${analysisId}`,
    {
      method: 'GET',
      credentials: 'include',
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  return response.json();
}

/**
 * Get analysis status and progress
 */
export async function getSurveyAnalysisStatus(
  surveyId: number,
  analysisId: number,
): Promise<SurveyAnalysisStatus> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/analysis/${analysisId}/status`,
    {
      method: 'GET',
      credentials: 'include',
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  return response.json();
}

/**
 * Get analysis history for a survey
 */
export async function getSurveyAnalysisHistory(surveyId: number): Promise<{
  analyses: AnalysisHistoryItem[];
}> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/analysis/history`,
    {
      method: 'GET',
      credentials: 'include',
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  return response.json();
}

/**
 * Poll for analysis completion
 */
export async function pollSurveyAnalysisCompletion(
  surveyId: number,
  analysisId: number,
  options: {
    timeout?: number;
    pollInterval?: number;
    onProgress?: (progress: number, message: string) => void;
  } = {},
): Promise<SurveyAnalysisResult> {
  const { timeout = 600000, pollInterval = 3000, onProgress } = options;
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    const status = await getSurveyAnalysisStatus(surveyId, analysisId);

    if (onProgress) {
      onProgress(status.progress, status.progress_message || '');
    }

    if (status.status === 'completed') {
      return getSurveyAnalysisById(surveyId, analysisId);
    }

    if (status.status === 'failed') {
      throw new Error(status.error_message || 'Analysis failed');
    }

    await new Promise((resolve) => setTimeout(resolve, pollInterval));
  }

  throw new Error(`Analysis timeout after ${timeout / 1000} seconds`);
}

// =============================================================================
// LINKEDIN VERIFICATION API
// =============================================================================

export interface LinkedInVerification {
  id: number;
  survey_id: number;
  session_id: number | null;
  linkedin_profile_url: string;
  linkedin_user_id: string | null;
  full_name: string | null;
  headline: string | null;
  location: string | null;
  connections_count: number;
  profile_picture_url: string | null;
  company: string | null;
  job_title: string | null;
  is_eligible: boolean;
  verification_status: string;
  rejection_reason: string | null;
  created_at: string;
  verified_at: string | null;
}

export interface LinkedInVerificationListResponse {
  verifications: LinkedInVerification[];
  total: number;
}

/**
 * Get LinkedIn verifications for a survey
 */
export async function getLinkedInVerifications(
  surveyId: number,
  limit: number = 100,
  offset: number = 0,
): Promise<LinkedInVerificationListResponse> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/linkedin/surveys/${surveyId}/verifications?limit=${limit}&offset=${offset}`,
    {
      method: 'GET',
      credentials: 'include',
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  return response.json();
}

/**
 * Generate LinkedIn share URL for a survey
 * This URL will initiate the LinkedIn verification flow
 */
export function getLinkedInShareUrl(shareId: string): string {
  return `${window.location.origin}/surveys/${shareId}/linkedin`;
}

/**
 * Check if a survey has LinkedIn verification enabled
 */
export function hasLinkedInVerification(survey: Survey | null): boolean {
  if (!survey) return false;
  const minConnections = (survey as any).min_linkedin_connections;
  return minConnections != null && minConnections > 0;
}

/**
 * Serve the next turn of an `ai_dynamic_section`, or learn that it is done.
 *
 * MUST go through `sessionFetch`, not a bare `fetch` - `require_session` on this route returns
 * 404 without the session token header, which only `sessionFetch` attaches.
 */
export async function getNextDynamicQuestion(
  sessionId: number,
  questionId: string,
): Promise<DynamicSectionNextResponse> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/dynamic-section/${questionId}/next`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || errorData.detail || `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  return response.json();
}

/**
 * Persist one respondent answer to an `ai_dynamic_section` turn.
 *
 * MUST go through `sessionFetch`, not a bare `fetch` - see `getNextDynamicQuestion` above.
 */
export async function submitDynamicAnswer(
  sessionId: number,
  questionId: string,
  answer: DynamicSectionAnswerPayload,
): Promise<DynamicSectionAnswerResponse> {
  const response = await sessionFetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/dynamic-section/${questionId}/answer`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(answer),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || errorData.detail || `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  return response.json();
}
